import { GiftInvitationPrompt } from '@src/pages/BoxPage/GiftInvitationPrompt';
import { ResumeCopyGame } from '@src/pages/BoxesPage/DuplicateGameDialog';
import { SupportAccessPage } from '@src/pages/RecoverPage/SupportAccessPage';
import { store } from '@src/state/store';
import { PremiumLocalCheckoutPage } from '@src/pages/PremiumPage/PremiumLocalCheckoutPage';
import { AdminPage } from '@src/pages/AdminPage/AdminPage';
import { TermsPage } from '@src/pages/TermsPage/TermsPage';
import { PremiumBuyPage } from '@src/pages/PremiumPage/PremiumBuyPage';
import { migrateCardAccessPath } from '@src/pages/BoxPage/cardAccess';
import { UserAppearance } from '@src/state/UserAppearance';
import { NotificationsProvider } from '@src/components/Notifications/NotificationsContext';
import { NotificationsPage } from '@src/components/Notifications/NotificationsPage';
import { NotificationAccessPage } from '@src/pages/NotificationAccessPage/NotificationAccessPage';
import { AdsProvider } from '@src/components/Ads/Ads';
import { initUmami } from '@src/utils/umami';
import { CookieBanner } from '@src/components/CookieBanner/CookieBanner';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { ThemePreviewPage } from './pages/NewBoxPage/ThemePreviewPage';
import { useEffect } from 'react';
import { DialogHost, useDialogOpen } from '@src/components/Dialog/Dialog';
import { ToastHost } from '@src/components/Toast';
import { ProfilesPage } from '@src/pages/account/ProfilesPage';
import { AccountPage } from '@src/pages/account/AccountPage';
import { BoxesPage } from '@src/pages/BoxesPage/BoxesPage';
import { BoxPage } from '@src/pages/BoxPage/BoxPage';
// Gifts catalog is paused; keep its implementation for the next release.
// import { GiftsPage } from '@src/pages/GiftsPage/GiftsPage';
import { FaqPage } from '@src/pages/FaqPage/FaqPage';
import { HomePage } from '@src/pages/HomePage/HomePage';
import { NewBoxPage } from '@src/pages/NewBoxPage/NewBoxPage';
import { PremiumPage } from '@src/pages/PremiumPage/PremiumPage';
import { LandingPage } from '@src/pages/LandingPage/LandingPage';
import { LoginPage } from '@src/pages/LoginPage/LoginPage';
import { PolicyPage } from '@src/pages/PolicyPage/PolicyPage';
import { RandomizerPage } from '@src/pages/RandomizerPage/RandomizerPage';
import { RecoverPage } from '@src/pages/RecoverPage/RecoverPage';
import { RegisterPage } from '@src/pages/RegisterPage/RegisterPage';
import { UiKitPage } from '@src/pages/UiKitPage/UiKitPage';
import { RouterChrome } from '@src/routing/RouterChrome';
import {
  ACCOUNT_BOXES_PATH,
  ACCOUNT_PATH,
  ADMIN_PATH,
  ACCOUNT_PROFILES_PATH,
  BOX_NEW_PATH,
  FAQ_PATH,
  PREMIUM_PATH,
  LANDING_PATHS,
  LOGIN_PATH,
  POLICY_PATH,
  TERMS_PATH,
  PRIVACY_POLICY_PATH,
  RANDOMIZER_PATH,
  RECOVER_PATH,
  REGISTER_PATH,
  UI_KIT_PATH,
  shortGamePath,
} from '@src/routing/routes';
import { useBoxRoute } from '@src/routing/useBoxRoute';
import { useAppDispatch, useAppSelector } from '@src/state/hooks';
import { isGameDomainPath, mainSiteUrl, safeGameReturnUrl } from '@src/routing/boxDomainNavigation';
import { loadSession } from '@src/state/sessionSlice';

/**
 * Remount for a different game or explicit personal link so its credentials
 * are read afresh. Internal views share the mounted game and loaded state.
 */
function BoxRoute() {
  const route = useBoxRoute();
  const identity = route ? `${route.key}:${route.cardKey ?? ''}` : 'missing';
  return <BoxPage key={identity} />;
}

/** The product routes. Unknown paths fall through to home, as they did before. */
export default function App() {
  useEffect(() => {
    // The SSR CSS snapshot is only for first paint. Vite now owns the live
    // styles; retaining the snapshot makes deleted rules survive hot updates.
    if (import.meta.env.DEV) document.getElementById('ssr-dev-css')?.remove();
  }, []);
  const location = useLocation();
  const navigate = useNavigate();
  const user = useAppSelector((state) => state.session.user);
  const key = typeof window !== 'undefined' ? window.__ssBoxKey : undefined;
  const shortPath = key ? shortGamePath(location.pathname, key) : location.pathname;
  const redirect =
    key && !isGameDomainPath(location.pathname, key)
      ? mainSiteUrl(location.pathname + location.search + location.hash)
      : null;
  useEffect(() => {
    if (shortPath !== location.pathname) {
      migrateCardAccessPath(location.pathname, shortPath);
      navigate(shortPath + location.search + location.hash, { replace: true });
      return;
    }
    if (redirect) {
      const target = new URL(redirect);
      if (['/login', '/register', '/recover'].includes(location.pathname)) {
        let returnPath = '/';
        try {
          returnPath = sessionStorage.getItem('game-domain-last-path') || '/';
        } catch {
          /* Optional return context. */
        }
        const returnUrl = new URL(returnPath, window.location.origin);
        target.searchParams.set(
          'returnTo',
          safeGameReturnUrl(returnUrl.href) || new URL('/', window.location.origin).href,
        );
      }
      window.location.replace(target.href);
      return;
    }
    if (key) {
      try {
        sessionStorage.setItem(
          'game-domain-last-path',
          location.pathname + location.search + location.hash,
        );
      } catch {
        /* Optional return context. */
      }
      return;
    }
    try {
      const queryReturn = safeGameReturnUrl(new URLSearchParams(location.search).get('returnTo'));
      if (queryReturn) sessionStorage.setItem('game-domain-return', queryReturn);
      const saved = safeGameReturnUrl(sessionStorage.getItem('game-domain-return'));
      if (
        saved &&
        user &&
        !user.isGuest &&
        !['/login', '/register', '/recover', '/open', '/open/recover', '/recover/access'].includes(
          location.pathname,
        )
      ) {
        sessionStorage.removeItem('game-domain-return');
        window.location.replace(saved);
      }
    } catch {
      /* Browsers may disable session storage. */
    }
  }, [redirect, key, shortPath, navigate, location.pathname, location.search, location.hash, user]);
  if (redirect || shortPath !== location.pathname) return <main aria-busy="true" />;
  return location.pathname === '/theme-preview' ? <ThemePreviewPage /> : <ProductApp />;
}

function ProductApp() {
  const dialogOpen = useDialogOpen();
  const dispatch = useAppDispatch();

  // Asked once per page load: the header states who you are, and a box page
  // needs the session before it can tell an organizer from a visitor. SSR
  // renders signed-out, so this only ever runs in the browser.
  useEffect(() => {
    initUmami();
    if (store.getState().session.status === 'idle') void dispatch(loadSession());
  }, [dispatch]);

  return (
    <AdsProvider>
      <UserAppearance />
      <NotificationsProvider>
        <div className="app" inert={dialogOpen || undefined}>
          <RouterChrome />
          <Routes>
            <Route path="/open" element={<NotificationAccessPage />} />
            <Route path="/open/recover" element={<NotificationAccessPage />} />
            <Route path={UI_KIT_PATH} element={<UiKitPage />} />
            <Route path={LOGIN_PATH} element={<LoginPage />} />
            <Route path={REGISTER_PATH} element={<RegisterPage />} />
            <Route path="/recover/access" element={<SupportAccessPage />} />
            <Route path={RECOVER_PATH} element={<RecoverPage />} />
            <Route path={TERMS_PATH} element={<TermsPage />} />
            <Route path={POLICY_PATH} element={<PolicyPage />} />
            <Route path={PRIVACY_POLICY_PATH} element={<PolicyPage />} />
            {/* <Route path="/gifts" element={<GiftsPage />} /> */}
            <Route path={PREMIUM_PATH} element={<PremiumPage />} />
            <Route path="/premium/local-checkout" element={<PremiumLocalCheckoutPage />} />
            <Route path="/premium/buy" element={<PremiumBuyPage />} />
            <Route path={FAQ_PATH} element={<FaqPage />} />
            <Route path={RANDOMIZER_PATH} element={<RandomizerPage />} />
            <Route path="/account/notifications" element={<NotificationsPage />} />
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route path={ACCOUNT_PROFILES_PATH} element={<ProfilesPage />} />
            <Route path={ADMIN_PATH} element={<AdminPage />} />
            <Route path="/admin/goods" element={<AdminPage />} />
            <Route path={ACCOUNT_PATH} element={<AccountPage />} />
            <Route path={ACCOUNT_BOXES_PATH} element={<BoxesPage />} />
            {LANDING_PATHS.map((path) => (
              <Route key={path} path={path} element={<LandingPage />} />
            ))}
            <Route path={BOX_NEW_PATH} element={<NewBoxPage />} />
            <Route path="/box/:key/*" element={<BoxRoute />} />
            <Route
              path="/"
              element={
                typeof window !== 'undefined' && window.__ssBoxKey ? <BoxRoute /> : <HomePage />
              }
            />
            <Route
              path="*"
              element={
                typeof window !== 'undefined' && window.__ssBoxKey ? <BoxRoute /> : <HomePage />
              }
            />
          </Routes>
          <CookieBanner />
        </div>
        <ToastHost />
        <DialogHost />
        <ResumeCopyGame />
        <GiftInvitationPrompt />
      </NotificationsProvider>
    </AdsProvider>
  );
}
